import React, { useState } from "react";
import { Lock, Eye, EyeOff, CheckCircle, Circle, AlertCircle, KeyRound, Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { useAdminChangeUserPasswordMutation } from "../../../../store/apis/user.api";

interface ChangePasswordSectionProps {
  userId?: string;
  employeeName?: string;
  isViewMode?: boolean;
}

const ChangePasswordSection: React.FC<ChangePasswordSectionProps> = ({
  userId,
  employeeName,
  isViewMode = false,
}) => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [adminChangeUserPassword, { isLoading }] = useAdminChangeUserPasswordMutation();

  // Password validation rules
  const hasMinLength = newPassword.length >= 8;
  const hasUppercase = /[A-Z]/.test(newPassword);
  const hasLowercase = /[a-z]/.test(newPassword);
  const hasNumber = /[0-9]/.test(newPassword);
  const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(newPassword);
  const passwordsMatch = newPassword.length > 0 && newPassword === confirmPassword;

  const isPasswordValid =
    hasMinLength &&
    hasUppercase &&
    hasLowercase &&
    hasNumber &&
    hasSpecialChar &&
    passwordsMatch;

  const handleUpdatePassword = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setErrorMessage("");

    if (!userId) {
      setErrorMessage("User ID is missing.");
      return;
    }

    if (!hasMinLength || !hasUppercase || !hasLowercase || !hasNumber || !hasSpecialChar) {
      setErrorMessage(
        "Password must be at least 8 characters long and contain at least 1 uppercase letter, 1 lowercase letter, 1 number, and 1 special character.",
      );
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMessage("Passwords do not match.");
      return;
    }

    try {
      const response = await adminChangeUserPassword({
        userId,
        newPassword,
      }).unwrap();

      toast.success(response?.message || "Password updated successfully!");
      setNewPassword("");
      setConfirmPassword("");
      setErrorMessage("");
    } catch (err: unknown) {
      const apiError = err as { data?: { message?: string }; message?: string };
      const msg =
        apiError?.data?.message ||
        apiError?.message ||
        "Failed to update password. Please try again.";
      setErrorMessage(msg);
      toast.error(msg);
    }
  };

  if (isViewMode) {
    return null;
  }

  return (
    <section className="bg-slate-50/70 border border-slate-200 rounded-xl p-6 space-y-6 shadow-sm">
      <div className="flex items-center justify-between border-b border-slate-200 pb-4">
        <div className="flex items-center space-x-3">
          {/* <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center text-primary-700">
            <KeyRound className="w-5 h-5" />
          </div> */}
          <div>
            <h3 className="text-base font-semibold text-slate-900 mb-0">Change Password</h3>
            <p className="text-xs text-slate-500 mb-0">
              Set a new secure password for {employeeName ? <span className="font-medium text-slate-700">{employeeName}</span> : "this employee"}.
            </p>
          </div>
        </div>
      </div>

      {errorMessage && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center text-sm">
          <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
          <span>{errorMessage}</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form Inputs */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">
              New Password <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  if (errorMessage) setErrorMessage("");
                }}
                placeholder="Enter new password"
                className="w-full h-10 px-3 pr-10 text-sm border border-slate-300 rounded-lg bg-white text-slate-900 hover:border-slate-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 placeholder:text-slate-400 transition-colors"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 focus:outline-none"
                onClick={() => setShowNewPassword((prev) => !prev)}
                tabIndex={-1}
              >
                {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">
              Confirm New Password <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (errorMessage) setErrorMessage("");
                }}
                placeholder="Re-enter new password"
                className="w-full h-10 px-3 pr-10 text-sm border border-slate-300 rounded-lg bg-white text-slate-900 hover:border-slate-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 placeholder:text-slate-400 transition-colors"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 focus:outline-none"
                onClick={() => setShowConfirmPassword((prev) => !prev)}
                tabIndex={-1}
              >
                {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="pt-2">
            <button
              type="button"
              onClick={handleUpdatePassword}
              disabled={isLoading || !isPasswordValid}
              className={`inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-lg text-white transition-all shadow-sm ${
                isPasswordValid && !isLoading
                  ? "bg-primary-600 hover:bg-primary-700 active:bg-primary-800 cursor-pointer"
                  : "bg-slate-400 cursor-not-allowed opacity-75"
              }`}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Updating Password...
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4 mr-2" />
                  Update Password
                </>
              )}
            </button>
          </div>
        </div>

        {/* Password Strength Checklist */}
        <div className="bg-white border border-slate-200 rounded-lg p-4 self-start space-y-3">
          <p className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
            Password Requirements
          </p>
          <ul className="space-y-2 text-xs">
            <li className={`flex items-center ${hasMinLength ? "text-green-700 font-medium" : "text-slate-500"}`}>
              {hasMinLength ? (
                <CheckCircle className="w-4 h-4 mr-2 text-green-600 flex-shrink-0" />
              ) : (
                <Circle className="w-4 h-4 mr-2 text-slate-400 flex-shrink-0" />
              )}
              <span>At least 8 characters long</span>
            </li>
            <li className={`flex items-center ${hasUppercase ? "text-green-700 font-medium" : "text-slate-500"}`}>
              {hasUppercase ? (
                <CheckCircle className="w-4 h-4 mr-2 text-green-600 flex-shrink-0" />
              ) : (
                <Circle className="w-4 h-4 mr-2 text-slate-400 flex-shrink-0" />
              )}
              <span>Contains at least 1 uppercase letter (A-Z)</span>
            </li>
            <li className={`flex items-center ${hasLowercase ? "text-green-700 font-medium" : "text-slate-500"}`}>
              {hasLowercase ? (
                <CheckCircle className="w-4 h-4 mr-2 text-green-600 flex-shrink-0" />
              ) : (
                <Circle className="w-4 h-4 mr-2 text-slate-400 flex-shrink-0" />
              )}
              <span>Contains at least 1 lowercase letter (a-z)</span>
            </li>
            <li className={`flex items-center ${hasNumber ? "text-green-700 font-medium" : "text-slate-500"}`}>
              {hasNumber ? (
                <CheckCircle className="w-4 h-4 mr-2 text-green-600 flex-shrink-0" />
              ) : (
                <Circle className="w-4 h-4 mr-2 text-slate-400 flex-shrink-0" />
              )}
              <span>Contains at least 1 number (0-9)</span>
            </li>
            <li className={`flex items-center ${hasSpecialChar ? "text-green-700 font-medium" : "text-slate-500"}`}>
              {hasSpecialChar ? (
                <CheckCircle className="w-4 h-4 mr-2 text-green-600 flex-shrink-0" />
              ) : (
                <Circle className="w-4 h-4 mr-2 text-slate-400 flex-shrink-0" />
              )}
              <span>Contains at least 1 special character (!@#$%...)</span>
            </li>
            <li className={`flex items-center ${passwordsMatch ? "text-green-700 font-medium" : "text-slate-500"}`}>
              {passwordsMatch ? (
                <CheckCircle className="w-4 h-4 mr-2 text-green-600 flex-shrink-0" />
              ) : (
                <Circle className="w-4 h-4 mr-2 text-slate-400 flex-shrink-0" />
              )}
              <span>Passwords match</span>
            </li>
          </ul>
        </div>
      </div>
    </section>
  );
};

export default ChangePasswordSection;
